import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useApi } from "../context/ApiContext";
import {
  getResizedImageUrl,
  ImagePresets,
  getImageUrl,
} from "../utils/imageUtils";
import { Spinner } from "../components/ui/Spinner";
import { ProductCard } from "../components/ui/ProductCard";
import { Button } from "../components/ui/Button";
import { useCurrency } from "../context/CurrencyContext";
import { useCart } from "../context/CartContext";
import { PageZone } from "../components/pageBuilder/PageZone";
import { useStoreLogoPlaceholder } from "../hooks/useStoreLogoPlaceholder";
import {
  calculateAvailableInventory,
  isOutOfStock,
  calculateProductCardPrice,
  calculateProductCardCompareAtPrice,
} from "../utils/inventoryUtils";
import { useStore } from "../context/StoreContext";
import { getAbsoluteUrl } from "../utils/metaUtils";

interface Product {
  _id: string;
  title: string;
  handle: string;
  description: string;
  images?: Array<{ url: string; isPrimary?: boolean }>;
  variants?: Array<{ price: number; compareAtPrice?: number }>;
  basePrice?: number;
  compareAtPrice?: number;
}

const Home: React.FC = () => {
  const { t } = useTranslation();
  const { api } = useApi();
  const { currency, location } = useCurrency();
  const { addToCart } = useCart();
  const storeLogoPlaceholder = useStoreLogoPlaceholder();
  const { store } = useStore();
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const limit = 12;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const productsRes = await api.get(
        `/products?limit=${limit}&status=active&page=1`,
      );
      const products = productsRes.data.products || [];
      setFeaturedProducts(products);
      setHasMore(products.length < (productsRes.data.total || 0));
      setPage(1);
    } catch (err) {
      console.error("Failed to fetch home data:", err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchMoreProducts = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const response = await api.get(
        `/products?limit=${limit}&status=active&page=${page + 1}`,
      );
      const newProducts = response.data.products || [];
      const totalProducts = response.data.total || 0;

      setFeaturedProducts((prev) => {
        const updatedProducts = [...prev, ...newProducts];
        setHasMore(updatedProducts.length < totalProducts);
        return updatedProducts;
      });

      setPage((prev) => prev + 1);
    } catch (err) {
      console.error("Failed to fetch more products:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [api, page, loadingMore, hasMore]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /*  const _handleNewsletterSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!email) return;
  
  setNewsletterLoading(true);
  setNewsletterMessage("");
  
  try {
   // TODO: Implement newsletter subscription API endpoint
   // await api.post("/newsletter/subscribe", { email });
   setNewsletterMessage("Thank you for subscribing!");
   setEmail("");
  } catch (err) {
   setNewsletterMessage("Something went wrong. Please try again.");
  } finally {
   setNewsletterLoading(false);
   setTimeout(() => setNewsletterMessage(""), 5000);
  }
 }; */

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }
  // Prepare meta tags data
  const siteUrl = getAbsoluteUrl("/");
  const siteName = store?.name || "Ecommerce Store";
  const siteDescription = `${siteName} - Shop the latest products and collections`;
  const siteImage = getImageUrl(store?.logo, "/logo512.png");

  return (
    <div className="w-full">
      <Helmet>
        <title>{siteName}</title>
        <meta name="description" content={siteDescription} />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:title" content={siteName} />
        <meta property="og:description" content={siteDescription} />
        <meta property="og:image" content={siteImage} />
        <meta property="og:site_name" content={siteName} />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={siteUrl} />
        <meta name="twitter:title" content={siteName} />
        <meta name="twitter:description" content={siteDescription} />
        <meta name="twitter:image" content={siteImage} />
      </Helmet>
      {/* Hero Zone */}
      <PageZone
        zoneName="hero"
        pageType="home"
        fallback={<div className="w-full min-h-[60vh]" />}
      />

      {/* Features Zone */}
      <PageZone
        zoneName="features"
        pageType="home"
        fallback={<div className="w-full min-h-[320px]" />}
      />

      {/* Products Zone */}
      <PageZone
        zoneName="products"
        pageType="home"
        fallback={<div className="w-full min-h-[480px]" />}
      />

      {/* Hero Section - Full Width */}
      {/* <section className="w-full bg-gradient-to-br from-store-primary via-store-primary-dark to-store-primary text-white py-20 md:py-32">
    <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
     <h1 className="text-4xl md:text-6xl font-bold mb-6 text-store-accent">
      {t("home.welcome")}
     </h1>
     <p className="text-lg md:text-2xl mb-4 text-white/90">
      {t("home.subDescription")}
     </p>
     <p className="text-base md:text-xl mb-8 text-white/80 max-w-2xl mx-auto">
      {t("home.description")}
     </p>
     <Link
      to="/products"
      className="inline-block bg-store-accent text-store-primary px-8 py-4 font-semibold hover:bg-store-accent-light transition-all text-lg hover: transform hover:scale-[1.02]"
     >
      {t("home.startShopping")}
     </Link>
    </div>
   </section> */}

      {/* Categories Section - Full Width */}
      {/*    {categories.length > 0 && (
    <section className="w-full bg-gradient-to-b from-white to-store-background py-16 md:py-24">
     <div className="max-w-7xl mx-auto px-4 md:px-8">
      <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-store-primary">
       {t("home.categories")}
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
       {categories.map((category) => (
        <Link
         key={category._id}
         to={`/products?category=${category.slug}`}
         className="group relative overflow-hidden bg-white  hover: transition-all transform hover:scale-[1.02]"
        >
         {category.image?.url ? (
          <img
           src={category.image.url}
           alt={category.name}
           className="w-full h-48 object-cover group- transition-transform duration-300"
          />
         ) : (
          <div className="w-full h-48 bg-gradient-to-br from-store-accent-light to-store-background flex items-center justify-center">
           <FaBox className="text-4xl text-store-primary opacity-50" />
          </div>
         )}
         <div className="p-4 text-center">
          <h3 className="text-store-primary font-semibold text-sm md:text-base group-hover:text-store-accent transition-colors">
           {category.name}
          </h3>
         </div>
        </Link>
       ))}
      </div>
     </div>
    </section>
   )} */}

      {/* Newsletter Section - Full Width */}
      {/* <section className="w-full bg-store-primary text-white py-16 md:py-20">
    <div className="max-w-2xl mx-auto px-4 md:px-8 text-center">
     <h2 className="text-2xl md:text-3xl font-bold mb-4">
      {t("home.newsletterTitle")}
     </h2>
     <p className="text-white/80 mb-8 text-base md:text-lg">
      {t("home.newsletterDescription")}
     </p>
     <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
      <input
       type="email"
       value={email}
       onChange={(e) => setEmail(e.target.value)}
       placeholder={t("home.newsletterPlaceholder")}
       required
       className="flex-1 px-4 py-3 text-store-primary focus:outline-none focus:ring-2 focus:ring-store-accent"
      />
      <button
       type="submit"
       disabled={newsletterLoading}
       className="bg-store-accent text-store-primary px-8 py-3 font-semibold hover:bg-store-accent-light transition-all disabled:opacity-50"
      >
       {newsletterLoading ? t("common.loading") : t("home.newsletterSubscribe")}
      </button>
     </form>
     {newsletterMessage && (
      <p className="mt-4 text-store-accent">{newsletterMessage}</p>
     )}
    </div>
   </section> */}

      {/* Social Media Section - Full Width */}
      {/* <section className="w-full bg-store-background py-12 md:py-16">
    <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
     <h2 className="text-2xl md:text-3xl font-bold mb-8 text-store-primary">
      {t("home.followUs")}
     </h2>
     <div className="flex justify-center items-center gap-6 flex-wrap">
      <a
       href="https://facebook.com"
       target="_blank"
       rel="noopener noreferrer"
       className="text-store-primary hover:text-store-accent transition-colors transform "
       aria-label="Facebook"
      >
       <FaFacebook className="text-4xl md:text-5xl" />
      </a>
      <a
       href="https://instagram.com"
       target="_blank"
       rel="noopener noreferrer"
       className="text-store-primary hover:text-store-accent transition-colors transform "
       aria-label="Instagram"
      >
       <FaInstagram className="text-4xl md:text-5xl" />
      </a>
      <a
       href="https://youtube.com"
       target="_blank"
       rel="noopener noreferrer"
       className="text-store-primary hover:text-store-accent transition-colors transform "
       aria-label="YouTube"
      >
       <FaYoutube className="text-4xl md:text-5xl" />
      </a>
      <a
       href="https://tiktok.com"
       target="_blank"
       rel="noopener noreferrer"
       className="text-store-primary hover:text-store-accent transition-colors transform "
       aria-label="TikTok"
      >
       <FaTiktok className="text-4xl md:text-5xl" />
      </a>
      <a
       href="https://pinterest.com"
       target="_blank"
       rel="noopener noreferrer"
       className="text-store-primary hover:text-store-accent transition-colors transform "
       aria-label="Pinterest"
      >
       <FaPinterest className="text-4xl md:text-5xl" />
      </a>
     </div>
    </div>
   </section> */}

      {/* Featured Products Section - Full Width - Last Section */}
      {featuredProducts.length > 0 && (
        <section className="w-full bg-white py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center">
              {/* <p className="text-store-primary-light text-base md:text-lg max-w-2xl mx-auto">
        {t("home.customizableNote")}
       </p> */}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8 mb-12">
              {featuredProducts.map((product) => {
                const image =
                  product.images?.find((img) => img.isPrimary) ||
                  product.images?.[0];
                const imageUrl = image
                  ? getResizedImageUrl(
                      image.url,
                      ImagePresets.productCard,
                      storeLogoPlaceholder,
                    )
                  : storeLogoPlaceholder;

                // Calculate price with location and variant support
                const locationId = location?.locationId;
                const price = calculateProductCardPrice(product, locationId);
                const originalPrice =
                  calculateProductCardCompareAtPrice(product);

                // Calculate inventory for featured products (with location-specific inventory if available)
                const availableInventory = calculateAvailableInventory(
                  product,
                  undefined,
                  locationId,
                );
                const outOfStock = isOutOfStock(product, undefined, locationId);

                return (
                  <ProductCard
                    key={product._id}
                    id={product._id}
                    title={product.title}
                    price={price}
                    originalPrice={originalPrice}
                    image={imageUrl}
                    currency={currency}
                    description={product.description}
                    href={`/products/${product.handle || product._id}`}
                    onAddToCart={addToCart}
                    isOutOfStock={outOfStock}
                    inventoryQuantity={availableInventory}
                    product={product}
                    locationId={locationId}
                  />
                );
              })}
            </div>

            {/* Load More Button */}
            <div className="flex flex-col justify-center items-center gap-4 mt-8">
              {hasMore && (
                <Button
                  onClick={fetchMoreProducts}
                  disabled={loadingMore}
                  variant="secondary"
                  size="md"
                  loading={loadingMore}
                >
                  {t("products.loadMore") || "Load More"}
                </Button>
              )}
              {!hasMore && featuredProducts.length > 0 && (
                <>
                  <p className="text-sm text-store-primary-light">
                    {t("products.allProductsLoaded") || "All products loaded"}
                  </p>
                  <Link
                    to="/products"
                    className="px-6 py-2 bg-store-secondary text-white hover:bg-store-secondary-dark transition-colors font-medium text-sm"
                  >
                    {t("products.viewAllProducts") || "View All Products"}
                  </Link>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* CTA Zone */}
      <PageZone
        zoneName="cta"
        pageType="home"
        fallback={<div className="w-full min-h-[240px]" />}
      />
    </div>
  );
};

export default Home;
